/**
 * Recreate hand-off storage — organization scoping.
 *
 * "Recreate" captures one generation's inputs from the gallery into
 * sessionStorage and then several widgets pick the pieces up after they mount
 * and finish their own async loads (field lists, workflow details). That
 * hand-off therefore outlives the navigation out of the gallery, so an
 * organization switch in between would hydrate the NEXT tenant's (billed)
 * generation with the PREVIOUS tenant's raw settings and media URLs — and, for
 * `DynamicWidgetRenderer`, persist them under the new org's storage key.
 *
 * Two layers, because the consumers are not uniform:
 *
 *  1. `clearRecreateStorage` — every key is dropped wholesale on a genuine
 *     tenant switch (`OrganizationProvider`), plus `clearForeignRecreateStorage`
 *     on the INITIAL organization resolution, which drops only the blobs
 *     stamped for someone else.
 *
 *     The initial resolution needs its own pass because the provider's
 *     "previous organization" ref is in-memory while the blobs are in
 *     sessionStorage: a reload (F5, or the automatic `lazyWithRetry` /
 *     `ChunkErrorBoundary` reload after a deploy) resets the ref to null but
 *     NOT the blobs, and the organization it resolves to comes from
 *     `localStorage.currentOrganizationId` — which is shared across tabs, so a
 *     switch performed in ANOTHER tab lands here as an "initial" value rather
 *     than a switch. Purging everything on that transition is wrong (it is the
 *     commit the blob was written for); purging only foreign-stamped blobs is
 *     exactly right.
 *
 *  2. `isRecreateBlobForOrg` / `readRecreateBlobForOrg` — a per-blob stamp
 *     check in the consumers themselves (`DynamicWidgetRenderer`,
 *     `ImageGenerationContext`, the four media widgets; `PromptEditorV2Widget`
 *     inlines the equivalent check for `recreate-prompt-v2` alongside its
 *     workflow match). React flushes child effects BEFORE the provider's, so a
 *     consumer can read in the same commit as the switch — i.e. before the
 *     purge — and the stamp is what makes it fail closed.
 *
 * A missing stamp is rejected: every writer sets one, so an untagged blob can
 * only be a stale cross-deploy leftover, and failing closed costs at most one
 * re-click of Recreate.
 */

export const RECREATE_STORAGE_KEYS = [
  'recreate-settings',
  'recreate-dynamic-values',
  'recreate-prompt-v2',
  'recreate-media-urls',
  'recreate-slots-data',
  'recreate-collections',
] as const;

interface RemovableStorage {
  removeItem(key: string): void;
}

interface ReadableStorage extends RemovableStorage {
  getItem(key: string): string | null;
}

/** Drops every recreate hand-off blob. Never throws (private-mode storage). */
export function clearRecreateStorage(storage: RemovableStorage | null | undefined): void {
  if (!storage) return;
  for (const key of RECREATE_STORAGE_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // sessionStorage can throw in restricted contexts — nothing to do.
    }
  }
}

/**
 * Drops every recreate hand-off blob that is NOT stamped for `organizationId`
 * (including unparseable and unstamped ones), leaving this organization's own
 * blobs intact.
 *
 * For the initial `null → organization` resolution, where a wholesale purge
 * would throw away the hand-off the user just started — see the header comment.
 * Never throws.
 */
export function clearForeignRecreateStorage(
  storage: ReadableStorage | null | undefined,
  organizationId: number | null | undefined
): void {
  if (!storage) return;
  for (const key of RECREATE_STORAGE_KEYS) {
    try {
      const raw = storage.getItem(key);
      if (raw === null) continue;
      if (readRecreateBlobForOrg(raw, organizationId) === null) {
        storage.removeItem(key);
      }
    } catch {
      // sessionStorage can throw in restricted contexts — nothing to do.
    }
  }
}

/**
 * True only when `blob` carries an `organizationId` matching `organizationId`.
 * Fails closed on a missing/blank stamp and on an unknown current organization.
 */
export function isRecreateBlobForOrg(
  blob: unknown,
  organizationId: number | null | undefined
): boolean {
  if (!organizationId) return false;
  if (!blob || typeof blob !== 'object') return false;
  const stamped = (blob as { organizationId?: unknown }).organizationId;
  if (stamped === undefined || stamped === null || stamped === '') return false;
  return String(stamped) === String(organizationId);
}

/**
 * Parses a raw recreate blob and returns it only when it is stamped for
 * `organizationId`. Unparseable, unstamped and foreign blobs all collapse to
 * `null` — the caller drops the key either way, so a rejected blob never
 * lingers to be picked up by a later mount.
 *
 * For consumers that read a raw string out of sessionStorage themselves (the
 * media widgets); `isRecreateBlobForOrg` is the already-parsed equivalent.
 */
export function readRecreateBlobForOrg(
  raw: string | null | undefined,
  organizationId: number | null | undefined
): Record<string, unknown> | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecreateBlobForOrg(parsed, organizationId)) return null;
  return parsed as Record<string, unknown>;
}
